import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import React from "react";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import integrationService from "../../../service/integrationService";
import { toast } from "react-toastify";
import errorHandler from "../../../utils/errorHandler";
import publicService from "../../../service/publicService";
import AppSelectField from "../../../lib/components/AppSelectField";

export const businessSectors = [
  "Agriculture & Agribusiness",
  "Oil & Gas",
  "Manufacturing",
  "Financial Services",
  "Technology & Telecommunications",
  "Construction & Real Estate",
  "Transportation & Logistics",
  "Healthcare",
  "Education",
  "Retail & Consumer Goods",
  "Hospitality & Tourism",
  "Mining & Solid Minerals",
  "Energy & Utilities",
  "Professional & Business Services",
  "Media & Entertainment",
  "Government & Public Sector",
  "Export & International Trade",
  "Creative Industries",
  "Sports & Recreation",
  "Telecommunications",
  "Pharmaceuticals",
  "Aviation",
  "Maritime & Shipping",
  "Automotive",
  "Food & Beverage",
  "Textiles & Apparel",
  "Chemicals",
  "Environmental Services",
  "Security Services",
  "Telecommunications Infrastructure",
  "E-commerce",
  "Advertising & Marketing",
  "Legal Services",
  "Accounting & Auditing",
  "Insurance",
  "Real Estate",
  "Renewable Energy",
  "Waste Management",
  "Water & Sanitation",
  "Beauty & Personal Care",
  "Fashion",
  "Arts & Culture",
  "Tourism & Travel",
  "Construction Materials",
  "Logistics & Courier Services",
  "Agriculture Processing",
  "Fisheries & Aquaculture",
  "Forestry",
  "Other Services",
];

export const erpOptions = ["Tally", "Microsoft Dynamics", "SAP", "Quickbooks", "Xero", "Excel", "Others"];

const defaultValues = {
  phone: "",
  email: "",
  sector: "",
  erp: "",
  address: {
    address: "",
    city: "",
    state: "",
    country: "",
    zipCode: "10001",
  },
};

const businessInfoSchema = Yup.object({
  email: Yup.string().trim().email("Email must be valid").required("Email address is required"),
  phone: Yup.string().required().label("Phone Number"),
  sector: Yup.string().required().label("Sector"),
  erp: Yup.string().required("ERP is required"),
  address: Yup.object({
    address: Yup.string().required(),
    city: Yup.string().required(),
    state: Yup.string().required(),
    country: Yup.string().required(),
  }),
});

const BusinessInformation = ({ isOpen, onClose, setCurrentStep, id, integration, currentStep, organization }) => {
  const [values, setValues] = React.useState({
    stateId: "",
    countryId: "",
  });
  const [customErp, setCustomErp] = React.useState("");
  const [customSector, setCustomSector] = React.useState("");

  const { handleSubmit, formState, control, reset, watch, setValue } = useForm({
    mode: "all",
    resolver: yupResolver(businessInfoSchema),
    defaultValues: defaultValues,
  });

  const selectedErp = watch("erp");
  const selectedSector = watch("sector");

  function save(data) {
    if (data.erp === "Others" && !customErp.trim()) {
      toast.error("Please specify your ERP");
      return;
    }
    if (data.sector === "Other Services" && !customSector.trim()) {
      toast.error("Please specify your sector");
      return;
    }
    const payload = {
      ...data,
      skipTax: false,
      erp: data.erp === "Others" ? customErp : data.erp,
      sector: data.sector === "Other Services" ? customSector : data.sector,
    };
    mutation.mutate(payload);
  }

  const mutation = useMutation({
    mutationFn: (data) => integrationService.proceed(data, { integrationId: id, organizationId: integration?.organizationId }),
    onSuccess: ({ data }) => {
      toast.success("Business connected successfully...");
      reset(defaultValues);
      setCurrentStep((prev) => prev + 1);
    },
    onError: ({ response }) => errorHandler(response),
  });
  const countriesQuery = useQuery({
    enabled: isOpen,
    queryKey: ["countries"],
    queryFn: () => publicService.getCountries(),
  });

  const statesQuery = useQuery({
    queryKey: ["states", values.countryId],
    enabled: !!values.countryId,
    queryFn: () => publicService.getStates({ countryId: values.countryId }),
  });

  const errors = formState.errors;
  const statesMap = {};
  const countriesMap = {};
  let countries = countriesQuery?.data?.data?.data ?? [];
  let states = statesQuery?.data?.data?.data ?? [];
  // countries = countries?.filter((item) => item?.name?.toLowerCase().includes("nigeria"));

  for (const item of countries) {
    countriesMap[item.name] = item;
  }
  for (const item of states) {
    statesMap[item.name] = item;
  }

  React.useEffect(() => {
    if (!organization) return;
    reset({
      ...defaultValues,
      phone: organization?.phone ?? "",
      email: organization?.email ?? "",
      address: {
        address: organization?.address?.address ?? "",
        city: organization?.address?.city ?? "",
        state: organization?.address?.state ?? "",
        country: organization?.address?.country ?? "",
        zipCode: organization?.address?.zipCode ?? "10001",
      },
    });
    const countryId = countriesMap[organization?.address?.country]?.id;
    setValues((prev) => ({ ...prev, countryId: countryId }));
  }, [isOpen, organization, countries?.length]);

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  if (currentStep !== 3) return null;

  return (
    <Dialog
      onClose={handleClose}
      open={isOpen}
      maxWidth="sm"
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit(save),
        className: "w-100",
        style: { maxWidth: 550 },
      }}
    >
      <React.Fragment>
        <div className="d-flex justify-content-between align-items-center gap-4 p-3 py-2 pe-2">
          <div className="fs-5 fw-medium">Complete Organization Details</div>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>
        <Divider className="bg-secondary-subtle" />
      </React.Fragment>

      <DialogContent className="pb-5">
        <Box mb={3}>
          <label className="d-flex align-items-center">
            <span>
              Email: <span className="text-danger">*</span>{" "}
            </span>
          </label>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  type="email"
                  {...field}
                  size="small"
                  variant="outlined"
                  error={!!errors.email}
                  helperText={errors?.email?.message ?? null}
                />
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>
            Phone: <span className="text-danger">*</span>
          </label>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <TextField
                  {...field}
                  type="text"
                  size="small"
                  variant="outlined"
                  error={!!errors.phone}
                  helperText={errors?.phone?.message ?? null}
                />
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>
            Sector: <span className="text-danger">*</span>
          </label>
          <Controller
            name="sector"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors?.sector}>
                <AppSelectField
                  value={field.value}
                  placeholder="Business sector"
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);

                    // Clear custom sector when switching away from Other Services
                    if (value !== "Other Services") {
                      setCustomSector("");
                    }
                  }}
                  items={businessSectors?.map((item) => ({ label: item, value: item })) ?? []}
                />
                {errors?.sector ? <FormHelperText className="">{errors?.sector?.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
          {selectedSector === "Other Services" && (
            <Box mt={2}>
              <label>
                Specify Sector: <span className="text-danger">*</span>
              </label>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                placeholder="Enter business sector"
                value={customSector}
                onChange={(e) => setCustomSector(e.target.value)}
              />
            </Box>
          )}
        </Box>
        <Box mb={3}>
          <label>
            ERP: <span className="text-danger">*</span>
          </label>

          <Controller
            name="erp"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors?.erp}>
                <AppSelectField
                  value={field.value}
                  placeholder="Select ERP"
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);

                    // Clear custom ERP when switching away from Others
                    if (value !== "Others") {
                      setCustomErp("");
                    }
                  }}
                  items={erpOptions.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                />
                {errors?.erp && <FormHelperText>{errors.erp.message}</FormHelperText>}
              </FormControl>
            )}
          />
          {selectedErp === "Others" && (
            <Box mt={2}>
              <label>
                Specify ERP: <span className="text-danger">*</span>
              </label>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                placeholder="Enter ERP name"
                value={customErp}
                onChange={(e) => setCustomErp(e.target.value)}
              />
            </Box>
          )}
        </Box>

        <Box mb={3}>
          <label>
            House No. / Street Name: <span className="text-danger">*</span>
          </label>
          <Controller
            name="address.address"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  type="text"
                  {...field}
                  size="small"
                  variant="outlined"
                  error={!!errors.address?.address}
                  helperText={errors?.address?.address?.message ?? null}
                />
              </FormControl>
            )}
          />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box mb={3}>
              <label>
                Country: <span className="text-danger">*</span>
              </label>
              <Controller
                name="address.country"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth error={!!errors?.address?.country}>
                    <AppSelectField
                      isLoading={countriesQuery.isFetching}
                      value={field.value}
                      placeholder="Country"
                      items={countries?.map((item) => ({ label: item.name, value: item.name })) ?? []}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value);
                        const countryId = countriesMap[value]?.id;
                        setValues((prev) => ({ ...prev, countryId: countryId }));
                      }}
                    />
                    {errors?.address?.country ? <FormHelperText className="">{errors?.address?.country?.message}</FormHelperText> : null}
                  </FormControl>
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box mb={3}>
              <label>
                State: <span className="text-danger">*</span>
              </label>

              <Controller
                name="address.state"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth error={!!errors?.address?.state}>
                    <AppSelectField
                      isLoading={statesQuery.isFetching}
                      value={field.value}
                      placeholder="State"
                      items={states?.map((item) => ({ label: item.name, value: item.name })) ?? []}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    {errors?.address?.state ? <FormHelperText className="">{errors?.address?.state?.message}</FormHelperText> : null}
                  </FormControl>
                )}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box mb={3}>
              <label>
                City: <span className="text-danger">*</span>
              </label>
              <Controller
                name="address.city"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <TextField
                      type="text"
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={field.value}
                      placeholder=""
                      onChange={(e) => field.onChange(e.target.value)}
                      helperText={errors["address"]?.city?.message}
                      error={!!errors["address"]?.city}
                    />
                  </FormControl>
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box mb={3}>
              <label>
                Zip Code: <span className="text-danger">*</span>
              </label>
              <Controller
                name="address.zipCode"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <TextField
                      type="text"
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={field.value}
                      placeholder=""
                      onChange={(e) => field.onChange(e.target.value)}
                      helperText={errors["address"]?.zipCode?.message}
                      error={!!errors["address"]?.zipCode}
                    />
                  </FormControl>
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => setCurrentStep((prev) => prev - 1)} // Go back one step
          style={{
            color: "#000", // Black text
            borderColor: "#000", // Black border
            backgroundColor: "#fff", // White background
            minWidth: 120,
          }}
        >
          Back
        </Button>
        <Button variant="contained" color="secondary" type="submit" disabled={mutation.isPending} style={{ minWidth: 120 }}>
          {mutation.isPending ? "Submitting..." : "Proceed"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessInformation;
